import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Editor from 'draft-js-plugins-editor';
import {
	EditorState,
	DraftHandleValue,
	convertToRaw,
	AtomicBlockUtils,
	RichUtils,
	ContentBlock,
	SelectionState,
	Modifier,
	ContentState,
	getDefaultKeyBinding,
} from 'draft-js';
import createImagePlugin from 'draft-js-image-plugin';
import { GreenroomState } from '../../../stores/types/types';
import { TeleprompterSectionInterface } from '../../../types/interfaces';
import Socket from '../../../connection/socket';
import 'draft-js/dist/Draft.css';
import { UploadImage } from '../../../connection/upload-image';
import { UploadDocument } from '../../../connection/upload-document';
import colors from '../../../utils/colors';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import RichTextEditorControls from '../../general-components/rich-text-editor-controls';
import useTeleprompters from '../../../hooks/use-teleprompters';
import Portal from '../../general-components/portal';
import { KEYS } from '../../../utils/keyboard-handler';
import { updateTeleprompterSection } from '../../../stores/actions/teleprompters';

const imagePlugin = createImagePlugin();

const fontColors: any = {
	WHITE: { color: colors.nearWhite },
	BLUE: { color: colors.textBlue },
	GREEN: { color: colors.textGreen },
	YELLOW: { color: colors.textYellow },
};


interface StyleMap {
	[key: string]: Object;
}

interface Props {
	teleprompterId: number;
	sectionId: number;
	teleprompterSection: TeleprompterSectionInterface;
	selectedSection: boolean;
	setSelectedSection: Function;
	socket: Socket | null;
	editorState: EditorState;
	setEditorState: React.Dispatch<React.SetStateAction<EditorState>>;
}

const styleMap = (function () {
	const len = 48;
	const map: StyleMap = {};

	// create font size map
	for (let i = 10; i <= len; i += 1) {
		map[i] = { fontSize: i };
	}

	return {
		...map,
		...fontColors,
	};
}());

const getBlockStyle = (block: ContentBlock): string => block.getType();

const blockAlignments = ['align-left', 'align-center', 'align-right'];

export default function TeleprompterSectionEditor(props: Props) {
	const {
		teleprompterId,
		sectionId,
		teleprompterSection,
		teleprompterSection: {
			content,
		},
		selectedSection,
		setSelectedSection,
		socket,
		editorState,
		setEditorState,
	} = props;

	const {
		editTeleprompterSections,
	} = useTeleprompters(socket);

	const dispatch = useDispatch();

	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);

	const [fontPopoverOpen, setFontPopoverOpen] = useState(false);
	const [fontSize, setFontSize] = useState(16);
	const [fontPopoverHidden, setFontPopoverHidden] = useState(true);
	const [currentBlockAlignment, setCurrentBlockAlignment] = useState(0);

	// Derive activeStates synchronously from editorState so the toolbar always
	// reflects the current cursor/selection without any async effect lag.
	const activeStates = useMemo<string[]>(() => {
		// @ts-ignore
		return Array.from(editorState.getCurrentInlineStyle().values()) as string[];
	}, [editorState]);

	const editor = useRef<any>(null);
	const currentContent = useRef<any>(null);
	const saveTime = useRef<NodeJS.Timeout | null>(null);
	const isSaving = useRef<boolean>(false);

	function focusEditor() {
		editor.current?.editor?.editor?.focus({
			preventScroll: true,
		});
	}

	useEffect(() => {
		currentContent.current = editorState;
	}, [editorState]);

	// Sync fontSize from editorState whenever it changes (e.g. socket/save resets).
	useEffect(() => {
		// @ts-ignore
		const states = Array.from(editorState.getCurrentInlineStyle().values()) as string[];
		setFontSize(Number(states.find((st: string) => !Number.isNaN(Number(st))) || 16));
	}, [editorState]);

	useEffect(() => {
		focusEditor();
	}, [teleprompterId]);

	useEffect(() => {
		/* eslint-disable-next-line @typescript-eslint/no-use-before-define */
		window.addEventListener('mousedown', handleClick);

		return () => {
			/* eslint-disable-next-line @typescript-eslint/no-use-before-define */
			window.removeEventListener('mousedown', handleClick);
		};
	}, [teleprompterId]);

	function insertImage(url: string): EditorState {
		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity(
			'image',
			'IMMUTABLE',
			{ src: url },
		);
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = EditorState.set(editorState, {
			currentContent: contentStateWithEntity,
		});
		return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
	}

	function handlePastedFiles(files: File[]): DraftHandleValue {
		for (let i = 0; i < files.length; i += 1) {
			const file = files[i];
			if (file.type.includes('image')) {
				UploadImage(token, file).then((img) => {
					setEditorState(insertImage(img.url));
				});
			} else {
				UploadDocument(token, file)
					.catch(console.error);
			}
		}
		return 'not-handled';
	}

	function handleDroppedFiles(selection: SelectionState, files: File[]): DraftHandleValue {
		// let key = selection.getAnchorKey();

		if (!user) return 'not-handled';

		for (let i = 0; i < files.length; i += 1) {
			const file = files[i];
			if (file.type.includes('image')) {
				UploadImage(token, file).then((img) => {
					setEditorState(insertImage(img.url));
				});
			} else {
				UploadDocument(token, file)
					.catch(console.error);
			}
		}
		return 'not-handled';
	}

	function handlePastedText(text: string): DraftHandleValue {
		try {
			const pastedBlocks = ContentState.createFromText(text, '\n\n').getBlockMap();
			const newState = Modifier.replaceWithFragment(
				editorState.getCurrentContent(),
				editorState.getSelection(),
				pastedBlocks,
			);

			const newEditorState = EditorState.push(editorState, newState, 'insert-fragment');

			if (newEditorState) {
				setEditorState(newEditorState);
				return 'handled';
			}

			return 'not-handled';
		} catch (e) {
			console.error(e);
			return 'not-handled';
		}
	}

	function handleFontStyle(e: any, style: number | string): DraftHandleValue {
		// cmd + B/I/U will send a string as e instead of click event
		if (typeof e !== 'string') {
			e.preventDefault();
		}

		const newState = RichUtils.toggleInlineStyle(editorState, String(style));

		if (newState) {
			setEditorState(newState);
			setTimeout(focusEditor, 16);
			return 'handled';
		}

		return 'not-handled';
	}

	function undo() {
		setEditorState(EditorState.undo(editorState));
	}

	function redo() {
		setEditorState(EditorState.redo(editorState));
	}

	function toggleUL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'unordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	function toggleOL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'ordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	// doesn't work for a block, it only works for either one line or the whole thing
	// lots of complaints in the draft.js repo over it
	function toggleAlignment(e: any) {
		e.preventDefault();
		const targetAlignment = (currentBlockAlignment + 1) % 3;

		const newState = RichUtils.toggleBlockType(editorState, blockAlignments[targetAlignment]);

		if (newState) {
			setEditorState(newState);
			setCurrentBlockAlignment(targetAlignment);

			return 'handled';
		}

		return 'not-handled';
	}

	async function handleSaveSection() {
		if (!teleprompterId) return;

		// If a user makes any changes, this timer starts running until it auto saves. However, if they
		// click out of this shot section editor prior to this timer completing, this function is
		// called to immediately save and we remove the timer set earlier for auto save.
		if (saveTime.current) clearTimeout(saveTime.current);

		const newContent = convertToRaw(currentContent.current.getCurrentContent());
		const sectionHasContent = currentContent.current.getCurrentContent().hasText();

		// if the content has not been updated, no need to save
		if (content === JSON.stringify(newContent)) return;

		// this prevents the creation of duplicates
		if (isSaving.current) return;
		isSaving.current = true;

		const updatedSection = {
			id: sectionId,
			teleprompter_id: teleprompterSection.teleprompter_id,
			section_data: { ...teleprompterSection.section_data, hasContent: sectionHasContent },
			content: JSON.stringify(newContent),
			section_type_id: teleprompterSection.section_type_id,
			section_type: teleprompterSection.section_type,
		};

		await editTeleprompterSections(
			teleprompterId,
			updatedSection,
		);

		isSaving.current = false;
	}

	function onChangeEditor(changedEditorState: EditorState) {
		setEditorState(changedEditorState);
		// @ts-ignore
		const inlineStyles = Array.from(changedEditorState.getCurrentInlineStyle().values()) as string[];
		setFontSize(Number(inlineStyles.find((st: string) => !Number.isNaN(Number(st))) || 16));

		const selection = changedEditorState.getSelection();
		const anchorKey = selection.getAnchorKey();
		const newContent = changedEditorState.getCurrentContent();
		const currentContentBlock = newContent.getBlockForKey(anchorKey);
		const blockType = currentContentBlock.getType();
		const blockAlignment = blockAlignments.findIndex((alignment) => alignment === blockType);

		if (blockAlignment > -1) setCurrentBlockAlignment(blockAlignment);

		if (saveTime.current) clearTimeout(saveTime.current);
		saveTime.current = setTimeout(handleSaveSection, 2000);
	}

	function toggleFontPopover() {
		if (fontPopoverOpen) {
			setFontPopoverOpen(false);
			setTimeout(() => {
				setFontPopoverHidden(true);
				focusEditor();
			}, 150);
		} else {
			setFontPopoverHidden(false);

			setTimeout(() => {
				setFontPopoverOpen(true);
				focusEditor();
			});
		}
	}

	function handleRangeChange(e: React.ChangeEvent<HTMLInputElement>) {
		setFontSize(Number(e.target.value));

		const selectionState = editorState.getSelection();
		const start = selectionState.getStartOffset();
		const end = selectionState.getEndOffset();

		// sizes - typescript doesn't recognize the iterator
		const fontSizeStates: string[] = Array.from(
			// @ts-ignore - get current inline styles, filter down to just font
			editorState.getCurrentInlineStyle().values(),
			// @ts-ignore
		).filter((st: string) => !Number.isNaN(Number(st)));

		// if input is selected
		if (end - start > 0) {
			// get current content
			let newContent = editorState.getCurrentContent();

			// iterate over font sizes to remove all font size styles from the block (can be multiples
			// if they select a range with multiple sizes)
			for (let i = 0; i < fontSizeStates.length; i += 1) {
				newContent = Modifier.removeInlineStyle(
					newContent,
					editorState.getSelection(),
					fontSizeStates[i] as string,
				);
			}

			// set the style for the selection
			newContent = Modifier.applyInlineStyle(
				newContent,
				editorState.getSelection(),
				e.target.value,
			);

			if (newContent) {
				// While dragging the letter sizer, onChangeEditor() wasn't firing continuously like it
				// normally does for every other change. So we add this saveSectionAfterInactivity()
				// here to make sure the timer begins when completing the font size change.
				setEditorState(EditorState.push(editorState, newContent, 'change-inline-style'));
				setTimeout(focusEditor, 16);
			}
		} else {
			let newEditor = editorState;

			// iterate and remove all existing font sizes
			fontSizeStates.forEach(
				(size) => {
					newEditor = RichUtils.toggleInlineStyle(editorState, size);
				},
			);

			const newState = RichUtils.toggleInlineStyle(newEditor, e.target.value);

			if (newState) {
				setEditorState(newState);
				setTimeout(focusEditor, 16);
			}
		}
	}

	function handleTab(e: any) {
		e.preventDefault();
		const currentState = editorState;

		const selection = currentState.getSelection();
		const blockType = currentState
			.getCurrentContent()
			.getBlockForKey(selection.getStartKey())
			.getType();

		if (blockType === 'unordered-list-item' || blockType === 'ordered-list-item') {
			onChangeEditor(RichUtils.onTab(e, currentState, 4));
		} else {
			const newContentState = Modifier.replaceText(
				currentState.getCurrentContent(),
				currentState.getSelection(),
				'    ',
			);

			onChangeEditor(EditorState.push(currentState, newContentState, 'insert-characters'));
		}
	}

	function myKeyBindingFn(e: any): string | null {
		if (e.keyCode === KEYS.KEY_TAB) {
			handleTab(e);
			return null;
		}
		return getDefaultKeyBinding(e);
	}

	function handleKeyCommand(command: any) {
		// if (command === 'myeditor-save') { return 'handled'; }

		if (command === 'italic') {
			handleFontStyle('event', 'ITALIC');
			return 'handled';
		}

		if (command === 'bold') {
			handleFontStyle('event', 'BOLD');
			return 'handled';
		}

		if (command === 'underline') {
			handleFontStyle('event', 'UNDERLINE');
			return 'handled';
		}

		// falls through to default command value
		return 'not-handled';
	}

	async function handleClick(e: any) {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (typeof path[i].className === 'string' && path[i].className.includes(`section-editor-${teleprompterSection.id}`)) {
				keepOpen = true;
				break;
			}
		}

		if (!keepOpen) {
			setSelectedSection(0);
			await handleSaveSection();
		}
	}

	// update the section locally in redux when the component unmounts.
	// other users receive the update from a socket message.
	useEffect(() => () => {
		const newContent = convertToRaw(currentContent.current.getCurrentContent());
		const sectionHasContent = currentContent.current.getCurrentContent().hasText();

		dispatch(updateTeleprompterSection(
			teleprompterId,
			{
				...teleprompterSection,
				id: sectionId,
				section_data: { ...teleprompterSection.section_data, hasContent: sectionHasContent },
				content: JSON.stringify(newContent),
			},
		));
	}, []);

	return (
		<div className={classNames('padding-top-small padding-bottom-medium width-full editor', `section-editor-${teleprompterSection.id}`)}>
			<div onClick={focusEditor}>
				<Editor
					ref={editor}
					editorState={editorState}
					onChange={onChangeEditor}
					plugins={[imagePlugin]}
					handlePastedFiles={handlePastedFiles}
					handlePastedText={handlePastedText}
					handleDroppedFiles={handleDroppedFiles}
					customStyleMap={styleMap}
					blockStyleFn={getBlockStyle}
					placeholder={'Start typing your text...'}
					readOnly={!selectedSection}
					keyBindingFn={myKeyBindingFn}
					handleKeyCommand={handleKeyCommand}
				/>
			</div>
			{/* this appends the editor toolbar below the sections when a section input is focused */}
			{!!selectedSection && (
				<Portal
					targetId="teleprompter-section-editor-controls"
					className={classNames('teleprompter-editor-controls', `section-editor-${teleprompterSection.id}`)}
				>
					<RichTextEditorControls
						undo={undo}
						redo={redo}
						handleFontStyle={handleFontStyle}
						toggleAlignment={toggleAlignment}
						toggleOL={toggleOL}
						toggleUL={toggleUL}
						toggleFontPopover={toggleFontPopover}
						activeStates={activeStates}
						currentBlockAlignment={currentBlockAlignment}
						fontPopoverOpen={fontPopoverOpen}
						fontPopoverHidden={fontPopoverHidden}
						fontSize={fontSize}
						handleRangeChange={handleRangeChange}
						fontColors={fontColors}
					/>
				</Portal>
			)}
		</div>
	);
}
